import React, { useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import { useNavigate, useParams } from 'react-router-dom';
import API_BASE_URL from '../Config';

const PlantillaDocumentoForm = () => {
    const { id } = useParams();
    const isEditing = !!id;
    const navigate = useNavigate();

    const [form, setForm] = useState({
        codigo: '',
        nombre: '',
        descripcion: '',
        contenido: '',
        activo: false
    });
    const [loading, setLoading] = useState(false);
    const [preview, setPreview] = useState(false);

    useEffect(() => {
        if (isEditing) fetchPlantilla();
    }, []);

    const fetchPlantilla = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/plantillas-documento/${id}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            setForm({
                codigo: data.codigo,
                nombre: data.nombre,
                descripcion: data.descripcion ?? '',
                contenido: data.contenido,
                activo: data.activo
            });
        } catch (error) {
            Swal.fire('Error', 'No se pudo cargar la plantilla', 'error');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.codigo.trim() || !form.nombre.trim() || !form.contenido.trim()) {
            Swal.fire('Atención', 'Código, nombre y contenido son obligatorios', 'warning');
            return;
        }

        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const url = isEditing
                ? `${API_BASE_URL}/plantillas-documento/${id}`
                : `${API_BASE_URL}/plantillas-documento`;
            const method = isEditing ? 'PUT' : 'POST';

            const response = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(form)
            });

            if (!response.ok) {
                const err = await response.json();
                throw new Error(err.message || 'Error al guardar la plantilla');
            }

            Swal.fire('Éxito', `Plantilla ${isEditing ? 'actualizada' : 'creada'} correctamente`, 'success');
            navigate('/parametrizacion/plantillas-documento');
        } catch (error) {
            Swal.fire('Error', error.message, 'error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="container-fluid py-5 px-lg-5" style={{ backgroundColor: '#f8fafc', minHeight: '100vh' }}>
            <div className="mb-4">
                <h2 className="fw-bolder text-dark mb-1">{isEditing ? 'Editar Plantilla' : 'Nueva Plantilla'}</h2>
                <p className="text-muted mb-0">Parametrización del sistema</p>
            </div>

            <div className="card border-0 shadow-sm rounded-4 p-4 bg-white">
                <form onSubmit={handleSubmit}>
                    <div className="row g-3 mb-3">
                        <div className="col-md-3">
                            <label className="form-label fw-bold">Código <span className="text-danger">*</span></label>
                            <input
                                type="text"
                                className="form-control rounded-pill"
                                placeholder="Ej: CONT-001"
                                maxLength={10}
                                value={form.codigo}
                                onChange={(e) => setForm({ ...form, codigo: e.target.value.toUpperCase() })}
                            />
                        </div>
                        <div className="col-md-6">
                            <label className="form-label fw-bold">Nombre <span className="text-danger">*</span></label>
                            <input
                                type="text"
                                className="form-control rounded-pill"
                                placeholder="Nombre de la plantilla"
                                value={form.nombre}
                                onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                            />
                        </div>
                        <div className="col-md-3 d-flex align-items-end pb-1">
                            <div className="form-check form-switch ms-2">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    role="switch"
                                    id="activoSwitch"
                                    checked={form.activo}
                                    onChange={(e) => setForm({ ...form, activo: e.target.checked })}
                                />
                                <label className="form-check-label fw-bold" htmlFor="activoSwitch">
                                    {form.activo ? 'Activo' : 'Inactivo'}
                                </label>
                            </div>
                        </div>
                    </div>

                    <div className="mb-3">
                        <label className="form-label fw-bold">Descripción <span className="text-muted fw-normal">(opcional)</span></label>
                        <input
                            type="text"
                            className="form-control rounded-pill"
                            placeholder="Breve descripción de la plantilla"
                            value={form.descripcion}
                            onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
                        />
                    </div>

                    <div className="mb-3">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <label className="form-label fw-bold mb-0">Contenido HTML <span className="text-danger">*</span></label>
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary rounded-pill px-3"
                                onClick={() => setPreview(!preview)}
                            >
                                <i className={`bi ${preview ? 'bi-code-slash' : 'bi-eye'} me-1`}></i>
                                {preview ? 'Ver código' : 'Vista previa'}
                            </button>
                        </div>

                        {preview ? (
                            <div
                                className="border rounded-3 p-3 bg-white"
                                style={{ minHeight: '300px' }}
                                dangerouslySetInnerHTML={{ __html: form.contenido }}
                            />
                        ) : (
                            <textarea
                                className="form-control font-monospace"
                                rows={12}
                                placeholder="<html>...</html>"
                                value={form.contenido}
                                onChange={(e) => setForm({ ...form, contenido: e.target.value })}
                                style={{ borderRadius: '12px', fontSize: '13px' }}
                            />
                        )}
                    </div>

                    <div className="d-flex gap-2 mt-4">
                        <button
                            type="submit"
                            className="btn btn-primary rounded-pill fw-bold px-4"
                            disabled={loading}
                        >
                            {loading ? <span className="spinner-border spinner-border-sm me-2"></span> : null}
                            {isEditing ? 'Actualizar' : 'Guardar'}
                        </button>
                        <button
                            type="button"
                            className="btn btn-outline-secondary rounded-pill px-4"
                            onClick={() => navigate('/parametrizacion/plantillas-documento')}
                        >
                            Cancelar
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default PlantillaDocumentoForm;